import React, { useState, useEffect } from "react";
import axios from "axios";
import { Instagram, ExternalLink, Heart, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstagramFeed() {
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [handle, setHandle] = useState("mamaminasoulfeed_");
  const [profileUrl, setProfileUrl] = useState("https://instagram.com/mamaminasoulfeed_");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/instagram/posts`)
      .then(({ data }) => {
        setPosts(data.items || []);
        setFeatured(data.featured || null);
        if (data.handle) setHandle(data.handle);
        if (data.profile_url) setProfileUrl(data.profile_url);
      })
      .catch(() => { setPosts([]); setFeatured(null); })
      .finally(() => setLoading(false));
  }, []);

  // Hide entire section on the home page if nothing to show — avoids an ugly
  // empty grid until the owner has curated at least one post.
  if (!loading && !featured && posts.length === 0) return null;

  return (
    <section
      id="instagram"
      className="py-24 md:py-36 border-t border-[var(--mm-line)]"
      data-testid="instagram-feed-section"
    >
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end mb-14 fade-in">
          <div className="md:col-span-8">
            <div className="section-label mb-4 flex items-center gap-2">
              <Instagram size={13} className="text-[var(--mm-accent)]" />
              Straight from the grid
            </div>
            <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
              @{handle} <span className="text-[var(--mm-accent)]">on the &apos;gram.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-follow-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Instagram size={14} /> Follow
            </a>
          </div>
        </div>

        {featured && (
          <FeaturedTile post={featured} handle={handle} />
        )}

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4"
          data-testid="instagram-grid"
        >
          {posts.map((p) => (
            <IgTile key={p.id} post={p} handle={handle} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedTile({ post, handle }) {
  const [broken, setBroken] = useState(false);
  const href = post.post_url || `https://instagram.com/${handle}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={`instagram-featured-${post.id}`}
      className="group relative block mb-4 md:mb-6 overflow-hidden bg-[var(--mm-bg-alt)] border border-[var(--mm-accent)]/60 hover:border-[var(--mm-accent)] transition-all duration-500"
    >
      <div className="grid md:grid-cols-2">
        <div className="aspect-square md:aspect-[4/5] overflow-hidden">
          {broken ? (
            <div className="w-full h-full flex items-center justify-center p-6 text-center">
              <div className="font-display italic text-[var(--mm-cream-muted)] text-sm">image unavailable</div>
            </div>
          ) : (
            <img
              src={post.image_url}
              alt={post.caption || `Featured post by @${handle}`}
              loading="lazy"
              onError={() => setBroken(true)}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          )}
        </div>
        <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-between bg-[var(--mm-bg-alt)] relative">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-6 px-3 py-1 border border-[var(--mm-accent)] text-[var(--mm-accent)] text-[0.6rem] tracking-[0.24em] uppercase">
              <Star size={10} className="fill-[var(--mm-accent)]" /> Featured post
            </div>
            {post.caption ? (
              <p className="font-display italic text-2xl md:text-3xl lg:text-4xl text-[var(--mm-cream)] leading-[1.2] font-light">
                &ldquo;{post.caption}&rdquo;
              </p>
            ) : (
              <p className="font-display italic text-2xl md:text-3xl text-[var(--mm-cream)] leading-[1.2] font-light">
                From the kitchen this week.
              </p>
            )}
          </div>
          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">
              @{handle}
            </div>
            <div className="inline-flex items-center gap-1.5 text-[var(--mm-accent)] text-[0.62rem] tracking-[0.24em] uppercase group-hover:gap-3 transition-all duration-300">
              <ExternalLink size={11} /> View on Instagram
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

function IgTile({ post, handle }) {
  const [broken, setBroken] = useState(false);
  const href = post.post_url || `https://instagram.com/${handle}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={`instagram-tile-${post.id}`}
      className="group relative block aspect-square overflow-hidden bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] hover:border-[var(--mm-accent)] transition-colors duration-500"
    >
      {broken ? (
        <div className="w-full h-full flex items-center justify-center p-4 text-center">
          <div className="font-display italic text-[var(--mm-cream-muted)] text-xs">
            image unavailable
          </div>
        </div>
      ) : (
        <img
          src={post.image_url}
          alt={post.caption || `Instagram post by @${handle}`}
          loading="lazy"
          onError={() => setBroken(true)}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
        />
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors duration-500 flex flex-col items-center justify-center gap-2 p-4 opacity-0 group-hover:opacity-100">
        {post.caption && (
          <div className="text-xs text-[var(--mm-cream)] leading-relaxed line-clamp-4 font-light">
            {post.caption}
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[var(--mm-accent)] text-[0.62rem] tracking-[0.24em] uppercase mt-1">
          <ExternalLink size={11} /> View on Instagram
        </div>
      </div>

      {/* corner heart icon */}
      <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Heart size={12} className="text-white fill-white" />
      </div>
    </a>
  );
}
